import React, { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { formatCountdown, RATE_LIMIT_CODE } from '@/lib/loginErrors';

// AUTH-2 — prominent inline panel for a temporary sign-in lock (429 TOO_MANY_ATTEMPTS).
// Counts down from the server-provided retry value and calls onExpire once it reaches zero.
export function SignInLockedPanel({ retryAfterSeconds, onExpire, testId = 'login-rate-limited' }) {
  const [expiresAt] = useState(() => Date.now() + Math.max(1, Number(retryAfterSeconds) || 1) * 1000);
  const [secondsLeft, setSecondsLeft] = useState(() => Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));

  useEffect(() => {
    const tick = () => setSecondsLeft(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  useEffect(() => {
    if (secondsLeft === 0 && onExpire) onExpire();
  }, [secondsLeft, onExpire]);

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid={testId}
      className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-950 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden />
        <div className="space-y-2">
          <p className="font-semibold leading-snug" data-testid={`${testId}-title`}>Too many unsuccessful sign-in attempts</p>
          <p className="text-sm leading-relaxed" data-testid={`${testId}-body`}>
            For your security, sign-in has been temporarily locked after multiple unsuccessful attempts.
            Please try again in{' '}
            <span className="font-semibold tabular-nums" data-testid={`${testId}-countdown`}>{formatCountdown(secondsLeft)}</span>.
            {' '}If you recently reset your password, wait until the temporary lock expires and then sign in using your new password.
          </p>
          <p className="text-[11px] text-amber-800/80" data-testid={`${testId}-reference`}>Reference: {RATE_LIMIT_CODE}</p>
        </div>
      </div>
    </div>
  );
}

export default SignInLockedPanel;
